import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { adminUserActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const userImportApi = createImportApi({
    actions: adminUserActions,
    capabilities,
    endpoint: 'admin/users',
    templateFileName: 'UserImportTpl'
});
export const ApiUsersImportCollection = userImportApi.importCollection;
export const ApiUsersDownloadImportModelItem = userImportApi.downloadImportModel;

export const ApiUsersGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post('admin/users/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.setList(response?.data?.list));
                    dispatch(adminUserActions.setCount(response?.data?.count));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('admin/users/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiUsersPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post('admin/users', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post('admin/users/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(adminUserActions.startExporting());
    }

    await gestopAPI
        .post('admin/users/export', { exportType, format, columns, filters, orderBy, groupBy }, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(adminUserActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(adminUserActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .put(`admin/users/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .patch(`admin/users/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersDeleteItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, updateStore = true, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post(`admin/users/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post(`admin/users/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersAlterUserIdentifierItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post(`admin/users/alteridentifier/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersResetUserPasswordItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post(`admin/users/resetpassword/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersLockItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, updateStore = true, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post(`admin/users/lock/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersUnlockItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, updateStore = true, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post(`admin/users/unlock/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersDeleteConnectionsItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .post(`admin/users/deleteconnections/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminUserActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminUserActions.startLoading());
    }

    await gestopAPI
        .get(`admin/users/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminUserActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminUserActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiUsersFileManagerUpload = async ({
    id,
    params = '',
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post(`admin/users/filemanager/upload/${id}?${params}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' } // Specific content type for file requests
        })
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};
